import { Box, Radio, Flex, Text, RadioGroup } from "@chakra-ui/react";
import { FieldValues, UseControllerProps, useController } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FlightSelectorFlight } from "../types/intake-funnel";
import {
  CANT_FIND_FLIGHT_ID,
  formatDate,
  isCantFindFlightChecked,
  sortFlightsByDepartureTime,
} from "v2/utils/flightSelectorHelpers";
import { AirplaneRight } from "@airhelp/icons";

type FlightSelectorControlProps<TFieldValues extends FieldValues> =
  UseControllerProps<TFieldValues> & {
    flights: Array<FlightSelectorFlight>;
    onChange: () => void;
  };

export function ControlledFlightSelector<T extends FieldValues>({
  name,
  rules,
  onChange,
  shouldUnregister,
  control,
  flights,
}: FlightSelectorControlProps<T>) {
  const { field } = useController({
    name,
    rules,
    shouldUnregister,
    control,
  });

  const { t } = useTranslation();
  return (
    <Box w="100%">
      <Flex justifyContent="space-between" p="8px 8px 8px 40px">
        <Text color="greyscale.500" fontSize="sm" fontWeight="medium" lineHeight="base">
          {t("steps.flights_list.scheduled_time")}
        </Text>
        <Text color="greyscale.500" fontSize="sm" fontWeight="medium" lineHeight="base">
          {t("steps.flights_list.flight_number")}
        </Text>
      </Flex>

      <Box zIndex={10} position={"relative"} bottom={0} textAlign={"left"}>
        <Box width="100%" mb="4">
          <RadioGroup>
            {sortFlightsByDepartureTime(flights).map((flight) => (
              <Box
                mb={2}
                key={flight.airlineIdentifier + flight.airlineCode + flight.flightNumber}
                data-testid="fs-flight"
              >
                <FlightItem
                  flight={flight}
                  onChange={() => {
                    onChange();
                    field.onChange(
                      flight.airlineIdentifier + flight.airlineCode + flight.flightNumber,
                    );
                  }}
                  value={field.value}
                  variant={"rounded"}
                />
              </Box>
            ))}
          </RadioGroup>
        </Box>
        <Box zIndex={10} position={"sticky"} bottom={0} textAlign={"left"} mt={-2}>
          <Box data-testid="cant-find-flight">
            <Radio
              onChange={() => {
                onChange();
                field.onChange(CANT_FIND_FLIGHT_ID);
              }}
              isChecked={isCantFindFlightChecked(field.value)}
              w="100%"
            >
              {t("steps.flights_list.cant_find")}
            </Radio>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function FlightItem({ flight, onChange, value, variant }: FlightItemProps) {
  const flightId = flight.airlineIdentifier + flight.airlineCode + flight.flightNumber;
  return (
    <Radio
      onChange={() => onChange(String(flightId))}
      isChecked={value === String(flightId)}
      width="100%"
      variant={variant}
    >
      <Flex direction="row" justify="space-between">
        <Flex
          direction={{ base: "column", md: "row" }}
          alignItems={{ base: "flex-start", md: "center" }}
        >
          <Box mr={{ md: 8 }}>
            {formatDate(flight.localDepartureAt)}
            <AirplaneRight color="greyscale.500" mx={2} verticalAlign="-2px" />
            {formatDate(flight.localArrivalAt)}
          </Box>
        </Flex>

        <Box>
          {flight.airlineCode} {flight.flightNumber}
        </Box>
      </Flex>
    </Radio>
  );
}

type FlightItemProps = {
  flight: FlightSelectorFlight;
  onChange: (value: string) => void;
  value?: string;
  variant?: string;
};
